import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { AbstractControl, FormGroup } from "@angular/forms";
import { Subscription } from "rxjs";
import { HttpService } from "../../services/http.service";
import { StateService } from "../../services/state.service";

interface SelectOption {
  name: string;
  value: string;
}

@Component({
  selector: "app-request-access-dialog",
  templateUrl: "./request-access-dialog.component.html",
  styleUrls: ["./request-access-dialog.component.scss"],
  standalone: false,
})
export class RequestAccessDialogComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() visible: boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  @Input() title: string = "Request Access";
  @Input() requestAccessForm!: FormGroup;

  /** Parent sets to true while its API call is in-flight */
  @Input() submitting: boolean = false;
  /** Parent sets to an error string if its API call fails, null otherwise */
  @Input() submitError: string | null = null;
  /** Parent sets to true after its API call succeeds to trigger success dialog */
  @Input() submitSuccess: boolean = false;
  /** The landing-page request is an onboarding flow; profile requests are not. */
  @Input() showWelcomeMessage = true;

  /** Emits { email, zone_id, countries } when the form is valid — parent handles the API call */
  @Output() submitted = new EventEmitter<{
    email: string;
    zone_id: string;
    countries: string[];
  }>();

  zoneOptions: SelectOption[] = [];
  countryOptions: SelectOption[] = [];
  zonesLoading = false;
  countriesLoading = false;

  showSuccessDialog: boolean = false;
  private dialogTimer: ReturnType<typeof setTimeout> | null = null;
  private zoneChangeSub: Subscription | null = null;

  constructor(
    private readonly httpService: HttpService,
    private readonly stateService: StateService,
  ) {}

  ngOnInit(): void {
    this.loadZones();
    this.bindZoneChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["submitSuccess"] && this.submitSuccess) {
      this.closeRequestDialog();
      this.openSuccessDialog();
    }
  }

  private loadZones(): void {
    this.zonesLoading = true;
    const url = this.stateService.apiList.admin.zones;
    this.httpService.getMethod(url).subscribe({
      next: (res: any) => {
        const list: any[] = Array.isArray(res) ? res : (res?.data ?? []);
        this.zoneOptions = list.map((z: any) => ({
          name: z.name ?? z.zone_name ?? z.label ?? String(z),
          value: z.id ?? z.zone_id ?? z.value ?? z.name ?? String(z),
        }));
        this.zonesLoading = false;
      },
      error: () => {
        this.zonesLoading = false;
      },
    });
  }

  ngOnDestroy(): void {
    if (this.dialogTimer) {
      clearTimeout(this.dialogTimer);
    }
    this.zoneChangeSub?.unsubscribe();
  }

  private bindZoneChanges(): void {
    const zoneCtrl = this.requestAccessForm?.get("zones");
    const countryCtrl = this.requestAccessForm?.get("countries");
    if (!zoneCtrl || !countryCtrl) return;

    countryCtrl.disable({ emitEvent: false });

    this.zoneChangeSub?.unsubscribe();
    this.zoneChangeSub = zoneCtrl.valueChanges.subscribe((zoneId: string) => {
      countryCtrl.patchValue([], { emitEvent: false });
      this.countryOptions = [];

      if (!zoneId) {
        countryCtrl.disable({ emitEvent: false });
        return;
      }

      countryCtrl.enable({ emitEvent: false });
      this.loadCountriesByZone(zoneId);
    });
  }

  private loadCountriesByZone(zoneId: string): void {
    this.countriesLoading = true;
    const url = this.stateService.apiList.admin.countriesByZones;
    this.httpService.postMethod(url, { zone_ids: [zoneId] }).subscribe({
      next: (res: any) => {
        const list: string[] = Array.isArray(res) ? res : [];
        this.countryOptions = list.map((country) => ({
          name: String(country),
          value: String(country),
        }));
        this.countriesLoading = false;
      },
      error: () => {
        this.countryOptions = [];
        this.countriesLoading = false;
      },
    });
  }

  onRequestDialogVisibilityChange(isVisible: boolean): void {
    this.visible = isVisible;
    this.visibleChange.emit(isVisible);
  }

  closeRequestDialog(): void {
    this.resetRequestForm();
    this.onRequestDialogVisibilityChange(false);
  }

  private resetRequestForm(): void {
    if (!this.requestAccessForm) {
      return;
    }

    const emailControl = this.requestAccessForm.get("email");
    const countryControl = this.requestAccessForm.get("countries");

    this.requestAccessForm.reset(
      {
        email: emailControl?.value ?? "",
        zones: "",
        countries: [],
      },
      { emitEvent: true },
    );
    this.requestAccessForm.markAsPristine();
    this.requestAccessForm.markAsUntouched();

    this.countryOptions = [];
    countryControl?.disable({ emitEvent: false });
  }

  submitRequest(): void {
    if (!this.requestAccessForm) {
      return;
    }

    this.requestAccessForm.markAllAsTouched();
    this.requestAccessForm.updateValueAndValidity();

    if (this.requestAccessForm.invalid) {
      return;
    }

    const { email, zones, countries } = this.requestAccessForm.getRawValue();
    this.submitted.emit({
      email,
      zone_id: zones,
      countries: Array.isArray(countries) ? countries : [],
    });
  }

  private openSuccessDialog(): void {
    this.showSuccessDialog = true;

    if (this.dialogTimer) {
      clearTimeout(this.dialogTimer);
    }

    this.dialogTimer = setTimeout(() => {
      this.showSuccessDialog = false;
    }, 3000);
  }

  hasError(controlName: string): boolean {
    const control = this.getControl(controlName);
    return !!control && control.invalid && (control.touched || control.dirty);
  }

  getErrorMessage(controlName: string): string {
    const control = this.getControl(controlName);
    if (!control || !control.errors) {
      return "";
    }

    if (control.errors["required"] || control.errors["minArrayLength"]) {
      switch (controlName) {
        case "email":
          return "ABInBev Official Email is required";
        case "zones":
          return "Please select a Zone";
        case "countries":
          return "Please select at least one Country";
        default:
          return "This field is required";
      }
    }

    if (control.errors["email"] || control.errors["pattern"]) {
      return "Please enter a valid ABInBev Official Email";
    }

    return "Invalid value";
  }

  private getControl(controlName: string): AbstractControl | null {
    return this.requestAccessForm?.get(controlName) ?? null;
  }
}
